import { useCallback, useEffect, useRef, useState } from "react";
import API, { setSeatLimitHandler } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Users, Minus, Plus, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { money } from "../lib/pricing";

const EVT = "fixli:add-seats";
/** Open the dialog from anywhere (e.g. the Billing page "Add seats" button). */
export function openAddSeats(opts = {}) { window.dispatchEvent(new CustomEvent(EVT, { detail: opts })); }

export default function AddSeatsDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [add, setAdd] = useState(1);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef(null);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  const finish = useCallback((ok) => {
    setOpen(false);
    if (resolver.current) { resolver.current(ok); resolver.current = null; }
  }, []);

  useEffect(() => {
    const onOpen = (e) => { setCtx({ ...(e.detail || {}), manual: true }); setAdd(e.detail?.add || 1); setOpen(true); };
    window.addEventListener(EVT, onOpen);
    if (isAdmin) {
      setSeatLimitHandler((info) => new Promise((resolve) => { resolver.current = resolve; setCtx(info); setAdd(1); setOpen(true); }));
    } else {
      setSeatLimitHandler(null);
    }
    return () => { window.removeEventListener(EVT, onOpen); setSeatLimitHandler(null); };
  }, [isAdmin]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null);
    API.get(`/billing/seats/preview?add=${add}`).then(({ data }) => { if (!cancelled) setPreview(data); }).catch(() => { if (!cancelled) setPreview({ error: true }); });
    return () => { cancelled = true; };
  }, [open, add]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/billing/seats", { add });
      toast.success(data.mode === "trial" ? `${data.seats} seats on your trial — you'll be charged for them when you subscribe` : `Seats added — you now have ${data.seats}. The prorated amount has been charged to your card.`);
      window.dispatchEvent(new CustomEvent("fixli:seats-changed", { detail: data }));
      finish(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add seats");
    } finally { setBusy(false); }
  };

  if (!isAdmin) return null;
  const cycleLabel = preview?.cycle === "annual" ? "yr" : "mo";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && finish(false)}>
      <DialogContent className="max-w-md" data-testid="add-seats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users size={18} /> {ctx?.manual ? "Add user seats" : "You're out of user seats"}</DialogTitle>
          <DialogDescription>
            {ctx?.manual
              ? "Grow your team — new seats are live straight away."
              : `All ${preview?.seats_now ?? ctx?.limit ?? ""} of your seats are in use. Add seats now and we'll carry on creating that user for you.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-semibold">Seats to add</p>
              <p className="text-[11px] text-muted-foreground">{preview?.per_seat_per_cycle != null ? `${money(preview.per_seat_per_cycle)}/seat/${cycleLabel}` : "£/seat"}{preview?.seats_now != null ? ` · ${preview.used ?? "–"} of ${preview.seats_now} in use` : ""}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAdd((n) => Math.max(1, n - 1))} disabled={add <= 1} data-testid="add-seats-minus"><Minus size={12} /></Button>
              <Input type="number" min={1} max={500} value={add} onChange={(e) => setAdd(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1)))} className="h-8 w-16 text-center" data-testid="add-seats-input" />
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAdd((n) => Math.min(500, n + 1))} data-testid="add-seats-plus"><Plus size={12} /></Button>
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1" data-testid="add-seats-preview">
            {!preview ? <p className="text-xs text-muted-foreground">Working out the price…</p>
              : preview.error ? <p className="text-xs text-destructive">Couldn't load pricing — you can still add seats.</p>
              : preview.unlimited ? <p className="text-xs text-muted-foreground">Your plan already has unlimited seats.</p>
              : (<>
                <div className="flex justify-between"><span className="text-muted-foreground">Seats after</span><span className="font-semibold" data-testid="add-seats-after">{preview.seats_after}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Charged today (prorated)</span><span className="font-semibold" data-testid="add-seats-prorated">{preview.mode === "trial" ? "Nothing — free on trial" : preview.prorated_now == null ? "Calculated by Stripe" : money(preview.prorated_now)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Then per {cycleLabel === "yr" ? "year" : "month"}</span><span className="font-semibold" data-testid="add-seats-recurring">{money(preview.recurring_after)} <span className="text-[11px] text-muted-foreground font-normal">(was {money(preview.recurring_before)})</span></span></div>
                {preview.renews_at && <p className="text-[11px] text-muted-foreground pt-1">Renews {new Date(preview.renews_at).toLocaleDateString("en-GB")}{preview.mode === "trial" ? " · seats are billed once you subscribe" : ""}</p>}
              </>)}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => finish(false)} disabled={busy} data-testid="add-seats-cancel">Not now</Button>
          <Button onClick={confirm} disabled={busy || preview?.unlimited} data-testid="add-seats-confirm"><Lightning size={14} className="mr-1" weight="fill" /> {busy ? "Adding…" : `Add ${add} seat${add === 1 ? "" : "s"}${preview?.mode === "immediate" && preview?.prorated_now != null ? ` · ${money(preview.prorated_now)} now` : ""}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
